import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { useSetupBridgeStore } from '@/rennovation/planning-view/store/bridge/setupBridge';
import { useDutyStore } from '@/rennovation/planning-view/store/useDutyStore';
import { ApiCreateDutyRequest } from '@/rennovation/planning-view/types/api/duties-request';
import { ApiDuty, DutyBreakForForm, DutyLabel } from '@/rennovation/planning-view/types/api/duties-response';
import { useDayStore } from '@/rennovation/planning-view/store/useDayStore';
import { RecentDutyItem } from '@/rennovation/planning-view/types/api/duties-recent-response';
import { DutyTemplateItem, TemplateQualification } from '@/rennovation/planning-view/types/api/duty-template-response';
import { usePlanningAssignDutyStore } from '@/rennovation/planning-view/store/usePlanningAssignDutyStore';
import { formatMinutesToTimeString, parseTimeToMinutes, parseTimeToSeconds } from '@/rennovation/shared/utils/time-utils';
import { useAlertStore } from '@/rennovation/planning-view/store/useAlertStore';
import { useI18n } from 'vue-i18n';
import useDateTime from '../../../../components/composables/useDateTime';
import { cloneDeep } from 'lodash';
import { PLANNING_DAY_STATUS } from '@/rennovation/planning-view/constants';

export type BlueprintRawPayload = RecentDutyItem | ApiDuty | DutyTemplateItem;

export interface SelectedTemplateAsBlueprint {
	_id: string;
	id: string;
	name: string;
	raw: BlueprintRawPayload;
	isRecentTrack?: boolean;
	isCurrentDayTrack?: boolean;
	driverName?: string;
	provider?: string;
	providerId?: string;
}

export enum Mode {
	Planning = 'planning',
	Template = 'template',
}

export const CREATE_DUTY_FORM_TYPE = {
	COPY: 'copy',
	WORK_DAY: 'work_day',
} as const;

export type CreateDutyFormType = (typeof CREATE_DUTY_FORM_TYPE)[keyof typeof CREATE_DUTY_FORM_TYPE];

export interface TemplateFormPayload {
	name: string;
	root: string;
	workTime: string;
	extraPayAccountQuantity: Record<string, number>;
	qualifications: string[];
	notes?: string;
	internalNotes?: string;
	labels?: DutyLabel[];
	startStop?: string;
	endStop?: string;
	start?: string;
	end?: string;
	standbyStart?: string;
	standbyEnd?: string;
	breaks?: DutyBreakForForm[];
	provider?: string;
	providerId?: string;
}

type FormBreakItem = {
	paid: boolean;
	start: string;
	end: string;
	duration: string;
};

export type RuleFunction = (v: string) => boolean | string;

const createDefaultFormState = () => ({
	driverName: '',
	user: null as string | null,
	day: null as string | null,
	type: CREATE_DUTY_FORM_TYPE.COPY as CreateDutyFormType,
	selectDutyFrom: 'Template',
	dutyOrDriverSelection: null as SelectedTemplateAsBlueprint | null,
	name: '',
	_id: '',
	id: '',
	root: null as string | null,
	provider: '',
	providerId: '',
	qualifications: [] as string[],
	startStop: '',
	start: null as string | null,
	endStop: '',
	end: null as string | null,
	standbyStart: null as string | null,
	standbyEnd: null as string | null,
	additionalWorkTime: null as string | null,
	workTime: null as string | null,
	notes: '',
	internalNotes: null as string | null,
	breaks: [] as Array<FormBreakItem>,
	extraPayAccountQuantity: {} as Record<string, number>,
	labels: [] as DutyLabel[],
});

export const usePlanningCreateDutyStore = defineStore('planningCreateDuty', () => {
	const { t } = useI18n();
	const assignDutyStore = usePlanningAssignDutyStore();
	const alertStore = useAlertStore();

	const isOpen = ref(false);
	const isEditMode = ref(false);
	const setupBridge = useSetupBridgeStore();
	const dutyStore = useDutyStore();
	const dayStore = useDayStore();

	const { formatSecondsDuration } = useDateTime();

	const mode = ref<Mode>(Mode.Planning);
	const hasBasicData = ref(true);
	const selectedDate = ref<string>('');
	const selectedTemplate = ref<DutyTemplateItem | null>(null);
	const selectedRootDuties = ref<ApiDuty[]>([]);

	const form = ref(createDefaultFormState());

	const currentDutyId = computed(() => form.value._id || form.value.id);

	const isTimeFieldDisabled = computed(() => {
		return form.value.type === CREATE_DUTY_FORM_TYPE.COPY || (mode.value === Mode.Template && !hasBasicData.value);
	});

	const isTypeCopy = computed(() => form.value.type === CREATE_DUTY_FORM_TYPE.COPY);

	const calculatedWorkTimeDisplay = computed(() => {
		const rawForm = form.value;

		if (!rawForm.start && !rawForm.end && !rawForm.additionalWorkTime) {
			return null;
		}

		const startMin = parseTimeToMinutes(rawForm.start);
		const endMin = parseTimeToMinutes(rawForm.end);
		const additionalMin = parseTimeToMinutes(rawForm.additionalWorkTime);

		const baseDutyMinutes = endMin - startMin;

		let totalUnpaidBreakMinutes = 0;
		rawForm.breaks.forEach((b) => {
			if (b.start && b.end && !b.paid) {
				const bStart = parseTimeToMinutes(b.start);
				const bEnd = parseTimeToMinutes(b.end);
				if (bEnd >= bStart) {
					totalUnpaidBreakMinutes += bEnd - bStart;
				}
			}
		});

		const finalWorkMinutes = baseDutyMinutes - totalUnpaidBreakMinutes + additionalMin;

		return formatMinutesToTimeString(finalWorkMinutes);
	});

	const extraPayFieldsWithLabels = computed(() => {
		const masterAccounts = setupBridge.extraPayAccounts || [];

		return masterAccounts.map((account) => {
			const accountNumber = String(account.number);
			return {
				accountNumber,
				label: `${account.name} (${account.number})`,
			};
		});
	});

	const getInitialExtraPay = (templateSource?: Record<string, number>): Record<string, number> => {
		const initialExtraPay: Record<string, number> = {};
		const masterAccounts = setupBridge.extraPayAccounts || [];
		masterAccounts.forEach((account) => {
			const accountNumber = String(account.number);
			initialExtraPay[accountNumber] = templateSource?.[accountNumber] ?? 0;
		});
		return initialExtraPay;
	};

	const getBasicDataPayload = () => {
		if (!hasBasicData.value) {
			return {
				start: null,
				end: null,
				standbyStart: null,
				standbyEnd: null,
				breaks: [],
				startStop: '',
				endStop: '',
			};
		}

		return {
			start: parseTimeToSeconds(form.value.start),
			end: parseTimeToSeconds(form.value.end),
			standbyStart: parseTimeToSeconds(form.value.standbyStart),
			standbyEnd: parseTimeToSeconds(form.value.standbyEnd),

			breaks: form.value.breaks.map((b) => ({
				start: parseTimeToSeconds(b.start) || null,
				end: parseTimeToSeconds(b.end) || null,
				duration: parseTimeToSeconds(b.duration) || null,
				paid: !!b.paid,
			})),

			startStop: form.value.startStop || '',
			endStop: form.value.endStop || '',
		};
	};

	const isPastOrPublishedDay = (day) => {
		if (!day || !dayStore.daysRef) return false;
		const status = dayStore.getDayStatus(dayStore.daysRef[day]);

		return [PLANNING_DAY_STATUS.PAST, PLANNING_DAY_STATUS.PUBLISHED].includes(status);
	};

	async function handleSaveAndCreate(): Promise<ApiDuty> {
		let rawForm = form.value;

		const selectedItemRaw = rawForm.dutyOrDriverSelection?.raw;
		let targetDayObjectId: string | undefined = undefined;
		if (mode.value === Mode.Planning) {
			targetDayObjectId = selectedItemRaw?.day ?? rawForm?.day ?? undefined;
		}

		const payload: ApiCreateDutyRequest = {
			name: rawForm.name || '',
			hasBasicData: hasBasicData.value,
			...(targetDayObjectId ? { day: targetDayObjectId } : {}),
			user: rawForm.user || null,
			notes: rawForm.notes || '',
			internalNotes: rawForm.internalNotes || null,
			...getBasicDataPayload(),
			workTime: null,
			additionalWorkTime: parseTimeToSeconds(rawForm.additionalWorkTime) || null,
			type: 'duty',
			qualifications: rawForm.qualifications.filter(Boolean),
			root: mode.value === Mode.Template && form.value.selectDutyFrom === 'Template' ? null : rawForm.root || null,
			labels: rawForm.labels.map((l) => (typeof l === 'string' ? l : l._id)),
			extraPayAccountQuantity: sanitizeExtraPay(rawForm.extraPayAccountQuantity),
			fixed: mode.value !== Mode.Template && isPastOrPublishedDay(targetDayObjectId) ? false : true,
			provider: rawForm?.provider ? rawForm?.provider : undefined,
			providerId: rawForm.providerId ? rawForm.providerId + '_copy' : undefined,
		};

		try {
			const newDuty = await dutyStore.createDuty(payload);
			alertStore.showAlert(t(mode.value === Mode.Template ? 'duty_template_create_success' : 'duty_create_success'), 'success');
			assignDutyStore.closeModal();
			return newDuty;
		} catch (error: any) {
			const fallbackMsg = 'Failed to create duty. Please check your system layout or try again.';
			const backendMsg = error?.response?.data?.message || error?.message;

			alertStore.showAlert(backendMsg || fallbackMsg, 'error');
			throw error;
		}
	}
	async function handleEditTemplate(changedFields: Object): Promise<ApiDuty> {
		const rawForm = form.value;
		const selectedItemRaw = rawForm.dutyOrDriverSelection?.raw;
		let targetDayObjectId: string | undefined = undefined;
		if (mode.value === Mode.Planning) {
			targetDayObjectId = selectedItemRaw?.day ?? rawForm?.day ?? undefined;
		}

		const payload: ApiCreateDutyRequest = {
			...rawForm, // Spreads all original fields
			name: rawForm.name || '',
			hasBasicData: hasBasicData.value,
			...(targetDayObjectId ? { day: targetDayObjectId } : {}),
			user: rawForm.user || null,
			notes: rawForm.notes || '',
			internalNotes: rawForm.internalNotes || null,
			...getBasicDataPayload(),
			workTime: parseTimeToSeconds(rawForm.workTime) || null,
			additionalWorkTime: parseTimeToSeconds(rawForm.additionalWorkTime) || null,
			type: 'duty',
			qualifications: rawForm.qualifications.map((q: any) => (typeof q === 'string' ? q : q?.id || q?._id)).filter(Boolean),
			root: rawForm.root || null,
			labels: rawForm.labels.map((l) => (typeof l === 'string' ? l : l._id)),
			extraPayAccountQuantity: sanitizeExtraPay(rawForm.extraPayAccountQuantity),
			fixed: (rawForm as any).fixed ?? false,
		};

		// Remove client-only UI helpers
		delete (payload as any).driverName;
		delete (payload as any).selectDutyFrom;
		delete (payload as any).dutyOrDriverSelection;

		try {
			const updatedDuty = await dutyStore.updateDuty(rawForm._id || rawForm.id, payload);

			if (selectedRootDuties.value.length > 0) {
				const updatedProperties = changedFields ?? payload;
				const rootDutiesPayload = selectedRootDuties.value.map((d) => {
					const rootPayload: Partial<ApiDuty> = {
						...updatedProperties,
						_id: d._id,
						day: d.day?._id || d.day,
						dayObject: null,
						id: d._id,
						user: d.user,
						fixed: d.fixed,
						root: d.root,
					};

					// Remove time and location properties completely if hasBasicData is false
					if (!hasBasicData.value) {
						delete rootPayload.start;
						delete rootPayload.end;
						delete rootPayload.standbyStart;
						delete rootPayload.standbyEnd;
						delete rootPayload.breaks;
						delete rootPayload.startStop;
						delete rootPayload.endStop;
					}

					return rootPayload;
				});

				await dutyStore.updateRootDuties(rootDutiesPayload);
			}

			alertStore.showAlert(t(mode.value === Mode.Template ? 'duty_template_update_success' : 'duty_update_success'), 'success');
			assignDutyStore.closeModal();
			return updatedDuty;
		} catch (error: any) {
			const fallbackMsg = 'Failed to update duty template. Please check your system layout or try again.';
			const backendMsg = error?.response?.data?.message || error?.message;

			alertStore.showAlert(backendMsg || fallbackMsg, 'error');
			throw error;
		}
	}
	// Helper to sanitize extra pay values
	const sanitizeExtraPay = (extraPayObj: Record<string, number>) => {
		const processed: Record<string, number> = {};
		Object.entries(extraPayObj || {}).forEach(([key, val]) => {
			processed[key] = Number(val) || 0;
		});
		return processed;
	};
	function applyTemplateToForm(payload: TemplateFormPayload): void {
		const allKeys = new Set([...Object.keys(form.value.extraPayAccountQuantity), ...Object.keys(payload.extraPayAccountQuantity)]);

		const quantities: Record<string, number> = {};
		allKeys.forEach((key) => {
			quantities[key] = payload.extraPayAccountQuantity[key] ?? form.value.extraPayAccountQuantity[key] ?? 0;
		});

		form.value = {
			...form.value,
			...payload,
			...(mode.value === Mode.Template && form.value.selectDutyFrom === 'Template' ? { root: null } : {}),
			extraPayAccountQuantity: quantities,
		};
		form.value.type = CREATE_DUTY_FORM_TYPE.WORK_DAY;
	}

	function resetForm(): void {
		form.value = createDefaultFormState();
		isEditMode.value = false;
	}

	function resetMode(): void {
		mode.value = Mode.Planning;
	}

	async function openWithContext(payload: { driverName: string; driverId: string; date: string | null }): Promise<void> {
		resetForm();
		resetMode();

		isOpen.value = true;
		isEditMode.value = false;
		form.value.driverName = payload.driverName;
		form.value.user = payload.driverId || '';

		if (setupBridge.setup.defaultDutyLabels?.length > 0) {
			await setupBridge.loadLabelsFullInfo(setupBridge.setup.defaultDutyLabels);
			form.value.labels = setupBridge.defaultLabels;
		} else {
			form.value.labels = [];
		}

		form.value.extraPayAccountQuantity = getInitialExtraPay();

		if (payload.date) {
			selectedDate.value = payload.date;
			form.value.day = dayStore.daysIdRef[payload.date] || '';
		} else {
			selectedDate.value = '';
			form.value.day = '';
		}
	}

	async function openForTemplateAuthoring(): void {
		resetForm();

		mode.value = Mode.Template;
		hasBasicData.value = true;
		isOpen.value = true;
		isEditMode.value = false;

		form.value.extraPayAccountQuantity = getInitialExtraPay();
		if (setupBridge.setup.defaultDutyLabels?.length > 0) {
			await setupBridge.loadLabelsFullInfo(setupBridge.setup.defaultDutyLabels);
			form.value.labels = setupBridge.defaultLabels;
		} else {
			form.value.labels = [];
		}
	}

	function openForTemplateEdit(template: DutyTemplateItem): void {
		selectedTemplate.value = template;
		resetForm();
		mode.value = Mode.Template;
		hasBasicData.value = template.hasBasicData ?? true;
		isOpen.value = true;
		isEditMode.value = true;

		const templateData = cloneDeep(template);
		delete (templateData as any).id;
		delete (templateData as any)._id;

		let mappedBreaks = cloneDeep(template.breaks) ?? [];
		mappedBreaks = mappedBreaks.map(({ start, end, duration, paid }) => ({
			start: formatSecondsDuration(start) || '',
			end: formatSecondsDuration(end) || '',
			duration: formatSecondsDuration(duration) || formatSecondsDuration(end - start) || '',
			paid: !!paid,
		}));

		form.value = {
			...createDefaultFormState(),
			...templateData,
			_id: template._id || template.id || '',
			id: template.id || template._id || '',
			hasBasicData: template.hasBasicData,
			user: template.user || null,
			day: template.day || null,
			type: template.type || CREATE_DUTY_FORM_TYPE.COPY,
			selectDutyFrom: 'Template',
			dutyOrDriverSelection: null,
			name: template.name || '',
			root: template.root || null,
			provider: template.provider || '',
			providerId: template.providerId || '',
			qualifications: (template.qualifications || []).map((q: TemplateQualification) => (typeof q === 'object' && q !== null ? q.id || q._id : q)),
			startStop: template.startStop || '',
			start: formatSecondsDuration(template.start) || null,
			endStop: template.endStop || '',
			end: formatSecondsDuration(template.end) || null,
			standbyStart: formatSecondsDuration(template.standbyStart) || null,
			standbyEnd: formatSecondsDuration(template.standbyEnd) || null,
			additionalWorkTime: formatSecondsDuration(template.additionalWorkTime) || null,
			workTime: formatSecondsDuration(template.workTime) || null,
			notes: template.notes || '',
			internalNotes: template.internalNotes || null,
			breaks: mappedBreaks,
			extraPayAccountQuantity: getInitialExtraPay(template.extraPayAccountQuantity),
			labels: template.labels || [],
		};
	}

	const closeModal = () => {
		selectedDate.value = null;
		resetForm();

		isOpen.value = false;
	};

	return {
		isOpen,
		isEditMode,
		form,
		selectedDate,
		calculatedWorkTimeDisplay,
		extraPayFieldsWithLabels,
		hasBasicData,

		mode,
		isTimeFieldDisabled,
		isTypeCopy,
		currentDutyId,
		selectedRootDuties,
		getBasicDataPayload,
		resetForm,
		applyTemplateToForm,
		handleSaveAndCreate,
		handleEditTemplate,
		openWithContext,
		openForTemplateAuthoring,
		openForTemplateEdit,
		closeModal,
	};
});
